import React, { useState } from 'react';
import { Platform, Pressable, type PressableProps, type ViewStyle } from 'react-native';
import { Box, useTheme } from 'app/shopify/theme';

export type ActionState = 'enabled' | 'hover' | 'pressed' | 'focus' | 'disabled' | 'loading';
export interface ActionInteraction {
  disabled: boolean;
  loading: boolean;
  hovered: boolean;
  pressed: boolean;
  focused: boolean;
}
export interface ActionProps extends Pick<
  PressableProps,
  'onPress' | 'onLongPress' | 'hitSlop' | 'accessibilityLabel' | 'accessibilityHint' | 'testID' | 'onFocus' | 'onBlur'
> {
  /** A frozen visual preview; enabled/undefined follows real interaction. */
  state?: ActionState;
  disabled?: boolean;
  loading?: boolean;
}
export interface ActionAppearance {
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  overlayColor?: string;
  opacity?: number;
}
interface ActionPressableProps extends ActionProps {
  selected?: boolean;
  accessibilityRole?: PressableProps['accessibilityRole'];
  accessibilityState?: PressableProps['accessibilityState'];
  fullWidth?: boolean;
  minHeight?: number;
  height?: number;
  width?: number;
  radius?: number;
  paddingHorizontal?: number;
  paddingVertical?: number;
  appearance: (interaction: ActionInteraction) => ActionAppearance;
  children: (interaction: ActionInteraction) => React.ReactNode;
}

/** Shared native/web interaction, semantics, and focus ring for action controls. */
export function ActionPressable({
  state,
  disabled = false,
  loading = false,
  selected,
  fullWidth = false,
  minHeight,
  height,
  width,
  radius,
  paddingHorizontal = 0,
  paddingVertical = 0,
  appearance,
  children,
  onFocus,
  onBlur,
  onPress,
  onLongPress,
  accessibilityRole = 'button',
  accessibilityState,
  ...props
}: ActionPressableProps) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const isDisabled = disabled || state === 'disabled';
  const isLoading = loading || state === 'loading';
  const blocked = isDisabled || isLoading;
  const interactive = state === undefined || state === 'enabled';
  const borderRadius = radius ?? theme.borderRadii['radius.full'];
  const focusInset = theme.spacing['spacing.general.x1'];
  const style: ViewStyle = {
    alignSelf: fullWidth ? 'stretch' : 'flex-start',
    maxWidth: '100%',
    width: fullWidth ? '100%' : width,
    borderRadius,
  };

  return (
    <Pressable
      {...props}
      accessibilityRole={accessibilityRole}
      accessibilityState={{
        ...accessibilityState,
        disabled: blocked,
        busy: isLoading,
        ...(selected === undefined ? {} : { selected }),
      }}
      disabled={blocked}
      onPress={blocked ? undefined : onPress}
      onLongPress={blocked ? undefined : onLongPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onFocus={(event) => {
        setFocused(true);
        onFocus?.(event);
      }}
      onBlur={(event) => {
        setFocused(false);
        onBlur?.(event);
      }}
      style={[style, Platform.OS === 'web' ? { outlineWidth: 0 } : undefined]}
    >
      {({ pressed }) => {
        const interaction = {
          disabled: isDisabled,
          loading: isLoading,
          pressed: !blocked && (interactive ? pressed : state === 'pressed'),
          hovered: !blocked && (interactive ? hovered && !pressed : state === 'hover'),
          focused: !blocked && (focused || state === 'focus'),
        };
        const visual = appearance(interaction);
        return (
          <Box
            style={{
              minHeight,
              height,
              width: width === undefined ? undefined : '100%',
              borderRadius,
              backgroundColor: visual.backgroundColor,
              opacity: visual.opacity,
              paddingHorizontal,
              paddingVertical,
            }}
            alignItems="center"
            justifyContent="center"
          >
            {visual.overlayColor ? (
              <Box
                pointerEvents="none"
                position="absolute"
                top={0}
                bottom={0}
                left={0}
                right={0}
                style={{ borderRadius, backgroundColor: visual.overlayColor }}
              />
            ) : null}
            {children(interaction)}
            {/* Figma borders are inside strokes: don't change intrinsic width on hover. */}
            {visual.borderWidth ? (
              <Box
                pointerEvents="none"
                position="absolute"
                top={0}
                bottom={0}
                left={0}
                right={0}
                style={{ borderRadius, borderColor: visual.borderColor, borderWidth: visual.borderWidth }}
              />
            ) : null}
            {interaction.focused ? (
              <Box
                pointerEvents="none"
                testID="action-focus-ring"
                position="absolute"
                top={-focusInset}
                bottom={-focusInset}
                left={-focusInset}
                right={-focusInset}
                style={{
                  borderRadius,
                  borderColor: theme.colors['border.focused'],
                  borderWidth: theme.borderWidths['border-width.emphasized'],
                }}
              />
            ) : null}
          </Box>
        );
      }}
    </Pressable>
  );
}
